import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useState } from 'react';

import { STATISTIC_CONST } from '@/constant';
import {
  useMyTaskFrameStateQuery,
  useMyWorkTaskObjectQuery,
  useMyWorkTaskPeriodMetricQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  Groups,
  History,
  VFlex,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  VideoLabel,
} from '@/package';
import type { FrameStateStatistic, Task } from '@/type';

import {
  calculatePeriodData,
  calculateTotalFrame,
  calculateTotalObject,
  calculateUserCount,
  filterUnknownClass,
} from '../Statistic';
import BarChart from '../Statistic/BarChart/BarChart';
import { DoughnutChart } from '../Statistic/DoughnutChart';
import { RangePicker } from '../Statistic/RangePicker';
import type { PeriodUnit } from '../Statistic/type';

const MyWorkTaskStatistics = ({ taskDetail }: { taskDetail: Task }) => {
  return (
    <VStack gap={1.5} className="h-full w-[972px] pt-4">
      <MyWorkTaskStatisticOverview taskDetail={taskDetail} />
      <MyWorkTaskPeriodLabeledObject taskDetail={taskDetail} />
    </VStack>
  );
};

const MyWorkTaskStatisticOverview = ({
  taskDetail: task,
}: {
  taskDetail: Task;
}) => {
  const { data: taskState } = useMyTaskFrameStateQuery(task.projectID, task.id);
  const { data: taskObject } = useMyWorkTaskObjectQuery(
    task.projectID,
    task.id,
    true,
  );

  const totalObject = calculateTotalObject(taskObject?.statExtendClass);
  const frameProgress = (taskState as FrameStateStatistic) ?? {
    prework: 0,
    working: 0,
    reject: 0,
    review: 0,
    reviewing: 0,
    done: 0,
  };
  const totalFrame = calculateTotalFrame(frameProgress);
  const totalUser = calculateUserCount(
    task?.userTask,
    (cur: { user?: { role?: string } }) => cur.user?.role,
  );

  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Overview</div>
      <div className="grid h-[190px] grid-cols-2 gap-[14px]">
        <VStack className="h-full w-full" gap={1}>
          <VFlex
            gap={0.5}
            className="h-[87px] w-full rounded-md px-4 py-2 shadow-sm"
            items="center"
          >
            <Groups className="size-8" />

            <VStack>
              <span className="text-body-lg text-gray-600">Members</span>
              <VFlex
                className="text-body-md text-gray-300"
                items="center"
                gap={0.25}
              >
                <span>Manager</span>
                <span className="text-body-lg font-semibold text-black">
                  {Intl.NumberFormat().format(totalUser.manager)}
                </span>
                <div className="h-4 w-px bg-gray-300" />
                <span>Labeler</span>
                <span className="text-body-lg font-semibold text-black">
                  {Intl.NumberFormat().format(totalUser.worker)}
                </span>
              </VFlex>
            </VStack>
          </VFlex>

          <VFlex className="h-[87px] w-full" gap={1}>
            <VFlex
              gap={0.5}
              className="w-full flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <VideoLabel className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Work Frames</span>
                <span className="text-body-lg font-semibold text-black">
                  {totalFrame}
                </span>
              </VStack>
            </VFlex>

            <VFlex
              gap={0.5}
              className="w-full flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <CenterFocusWeak className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">
                  Total Objects
                </span>
                <span className="text-body-lg font-semibold text-black">
                  {totalObject}
                </span>
              </VStack>
            </VFlex>
          </VFlex>
        </VStack>
        <VStack gap={0.5} className="rounded-md px-4 py-2 shadow-sm">
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total frames progress
            </VFlex>

            <div className="text-body-sm text-gray-600">Total {totalFrame}</div>
          </VFlex>
          <VFlex className="w-full justify-center">
            <DoughnutChart
              totalTask={totalFrame}
              taskProgress={{
                none: 0,
                pending: 0,
                ready: frameProgress.prework,
                progress:
                  frameProgress.working +
                  frameProgress.reviewing +
                  frameProgress.review +
                  frameProgress.reject,
                completed: frameProgress.done,
                fail: 0,
              }}
            />
          </VFlex>
        </VStack>
      </div>
    </VStack>
  );
};

const MyWorkTaskPeriodLabeledObject = ({
  taskDetail,
}: {
  taskDetail: Task;
}) => {
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('day');
  const [between, setBetween] = useState<[Dayjs, Dayjs]>([
    dayjs(STATISTIC_CONST.TODAY).subtract(12, 'week'),
    dayjs(STATISTIC_CONST.TODAY),
  ]);
  const { data: taskMetric } = useMyWorkTaskPeriodMetricQuery({
    projectID: taskDetail.projectID,
    taskID: taskDetail.id,
    period: periodUnit,
    between,
  });

  const extendClassList = taskMetric?.extendClass?.filter(filterUnknownClass);

  const periodData = calculatePeriodData(
    extendClassList,
    taskMetric?.userMetric,
  );

  return (
    <VStack gap={0.5} className="h-full w-full pb-[100px]">
      <div className="text-headline-md font-bold text-blue-300">
        Total labeled objects
      </div>

      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-sm font-semibold text-primary-500">
          Trend
        </div>
        <VFlex items="center" className="w-full" justify="end" gap={0.5}>
          <RangePicker
            value={between}
            onChange={setBetween}
            periodUnit={periodUnit}
            key={periodUnit}
          />

          <VTab
            defaultValue="day"
            size="small"
            value={periodUnit}
            variant="secondary"
            onValueChange={val => setPeriodUnit(val as PeriodUnit)}
          >
            <VTabTriggerContainer>
              <VTabTrigger value="day">Daily</VTabTrigger>
              <VTabTrigger value="week">Weekly</VTabTrigger>
              <VTabTrigger value="month">Monthly</VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </VFlex>

      {periodData.length > 0 && (
        <BarChart data={periodData} periodUnit={periodUnit} between={between} />
      )}
    </VStack>
  );
};

export default MyWorkTaskStatistics;
